import { Component, Input, Output, EventEmitter, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { DialogModule } from 'primeng/dialog';
import { ButtonModule } from 'primeng/button';
import { RippleModule } from 'primeng/ripple';
import { TableModule } from 'primeng/table';
import { TooltipModule } from 'primeng/tooltip';
import { ToastModule } from 'primeng/toast';
import { DividerModule } from 'primeng/divider';
import { SkeletonModule } from 'primeng/skeleton';
import { MessageService } from 'primeng/api';
import { NGXLogger } from 'ngx-logger';
import { Rendicontazione } from '../../../models/rendicontazione.model';
import { FirebaseStorageService } from '../../../services/firebase-storage.service';
import { LottieComponent, AnimationOptions } from 'ngx-lottie';

interface FileInfo {
  nome: string;
  url: string;
  dimensione: number;
  tipo: string;
  dataCaricamento?: Date;
  categoria: 'rendicontazione' | 'determina';
}

@Component({
  selector: 'app-lista-file-rendicontazione',
  templateUrl: './lista-file-rendicontazione.component.html',
  styleUrls: ['./lista-file-rendicontazione.component.css'],
  standalone: true,
  imports: [
    CommonModule,
    DialogModule,
    ButtonModule,
    RippleModule,
    TableModule,
    TooltipModule,
    ToastModule,
    DividerModule,
    SkeletonModule,
    LottieComponent
  ],
  providers: [MessageService]
})
export class ListaFileRendicontazioneComponent implements OnInit {
  @Input() visible: boolean = false;
  @Input() intervento: any;
  @Input() rendicontazione: Rendicontazione | null = null;
  @Output() visibleChange = new EventEmitter<boolean>();

  files: FileInfo[] = [];
  isLoading: boolean = false;
  hasLoadedOnce: boolean = false;
  showConfermaEliminazioneSingoloFile: boolean = false;
  eliminazioneSingoloFileInCorso: boolean = false;
  fileSelezionatoPerEliminazione: FileInfo | null = null;

  options: AnimationOptions = {
    path: '/assets/animations/spinner-downloading.json',
    loop: true,
    autoplay: true
  };

  constructor(
    private messageService: MessageService,
    private logger: NGXLogger,
    private firebaseStorageService: FirebaseStorageService
  ) {}

  ngOnInit() {}

  onShow() {
    if (this.rendicontazione && this.intervento) {
      this.caricaListaFile();
    }
  }

  onHide() {
    this.visibleChange.emit(false);
    this.files = [];
    this.hasLoadedOnce = false;
    this.isLoading = false;
  }

  caricaListaFile() {
    if (!this.rendicontazione || !this.intervento) {
      return;
    }

    this.isLoading = true;
    this.files = [];

    const pathRendicontazione = `interventi/${this.rendicontazione.intervento}/rendicontazioni/${this.rendicontazione.id}`;
    const pathDetermina = `interventi/${this.rendicontazione.intervento}/determine-umvs/${this.rendicontazione.id}`;

    // Array per tenere traccia delle chiamate completate
    let completedCalls = 0;
    const totalCalls = 2;

    const checkLoadingComplete = () => {
      if (completedCalls >= totalCalls) {
        // Aggiungi un piccolo ritardo per assicurarsi che processFiles sia completato
        setTimeout(() => {
          this.isLoading = false;
          this.hasLoadedOnce = true;
        }, 100);
      }
    };

    // Carica file di rendicontazione
    this.firebaseStorageService.listFiles(pathRendicontazione).subscribe({
      next: async (files: string[]) => {
        await this.processFiles(files, 'rendicontazione');
        completedCalls++;
        checkLoadingComplete();
      },
      error: (error) => {
        this.logger.error('[LISTA FILE] Errore nel caricamento file rendicontazione', error);
        completedCalls++;
        checkLoadingComplete();
      }
    });

    // Carica file di determina
    this.firebaseStorageService.listFiles(pathDetermina).subscribe({
      next: async (files: string[]) => {
        await this.processFiles(files, 'determina');
        completedCalls++;
        checkLoadingComplete();
      },
      error: (error) => {
        this.logger.error('[LISTA FILE] Errore nel caricamento file determina', error);
        completedCalls++;
        checkLoadingComplete();
      }
    });
  }

  private async processFiles(fileUrls: string[], categoria: 'rendicontazione' | 'determina') {
    for (const url of fileUrls) {
      try {
        const fileInfo = await this.extractFileInfo(url, categoria);
        this.files.push(fileInfo);
      } catch (error) {
        this.logger.error('[LISTA FILE] Errore nell\'elaborazione file', error);
      }
    }

    // Ordina i file per categoria e poi per nome
    this.files.sort((a, b) => {
      if (a.categoria !== b.categoria) {
        return a.categoria === 'rendicontazione' ? -1 : 1;
      }
      return a.nome.localeCompare(b.nome);
    });
  }

  private async extractFileInfo(url: string, categoria: 'rendicontazione' | 'determina'): Promise<FileInfo> {
    const nomeFile = this.extractFileNameFromUrl(url);
    const tipo = this.getFileType(nomeFile);

    let dimensione = 0;
    let dataCaricamento: Date | undefined;

    try {
      // Effettua una richiesta HEAD per ottenere i metadati
      const response = await fetch(url, { method: 'HEAD' });
      if (response.ok) {
        const contentLength = response.headers.get('content-length');
        if (contentLength) {
          dimensione = parseInt(contentLength, 10);
        }

        const lastModified = response.headers.get('last-modified');
        if (lastModified) {
          dataCaricamento = new Date(lastModified);
        }
      }
    } catch (error) {
      this.logger.warn('[LISTA FILE] Impossibile ottenere metadati per', nomeFile, error);
    }

    return {
      nome: nomeFile,
      url: url,
      dimensione: dimensione,
      tipo: tipo,
      dataCaricamento: dataCaricamento,
      categoria: categoria
    };
  }

  private extractFileNameFromUrl(filePath: string): string {
    try {
      const url = new URL(filePath);
      const pathSegments = url.pathname.split('/');
      const fileNameSegment = pathSegments[pathSegments.length - 1];
      const decodedPath = decodeURIComponent(fileNameSegment);
      const fileName = decodedPath.split('/').pop() || 'documento';
      return fileName;
    } catch (error) {
      this.logger.error('[LISTA FILE] Errore parsing URL', error);
      return 'documento_sconosciuto';
    }
  }

  private getFileType(fileName: string): string {
    const extension = fileName.split('.').pop()?.toLowerCase();

    const typeMap: {[key: string]: string} = {
      'pdf': 'PDF',
      'doc': 'Word',
      'docx': 'Word',
      'xls': 'Excel',
      'xlsx': 'Excel',
      'jpg': 'Immagine',
      'jpeg': 'Immagine',
      'png': 'Immagine',
      'gif': 'Immagine',
      'zip': 'Archivio',
      'rar': 'Archivio',
      'txt': 'Testo',
      'p7m': 'Firmato',
      'xml': 'XML',
      'csv': 'CSV'
    };

    return typeMap[extension || ''] || 'Documento';
  }

  formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 B';

    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));

    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  getFileIcon(tipo: string): string {
    const iconMap: {[key: string]: string} = {
      'PDF': 'pi-file-pdf',
      'Word': 'pi-file-word',
      'Excel': 'pi-file-excel',
      'Immagine': 'pi-image',
      'Archivio': 'pi-file-zip',
      'Testo': 'pi-file',
      'Firmato': 'pi-verified',
      'XML': 'pi-code',
      'CSV': 'pi-table'
    };

    return iconMap[tipo] || 'pi-file';
  }

  getCategoriaLabel(categoria: string): string {
    return categoria === 'rendicontazione' ? 'Rendicontazione' : 'Determina UMVS';
  }

  getCategoriaIcon(categoria: string): string {
    return categoria === 'rendicontazione' ? 'pi-euro' : 'pi-book';
  }

    scaricaFile(file: FileInfo) {
    this.logger.info('[LISTA FILE] Download file', file.nome);

    // Usa fetch per forzare il download invece dell'apertura nel tab
    fetch(file.url)
      .then(response => response.blob())
      .then(blob => {
        // Crea un URL per il blob
        const blobUrl = window.URL.createObjectURL(blob);

        // Crea un link temporaneo per il download
        const link = document.createElement('a');
        link.href = blobUrl;
        link.download = file.nome;

        // Forza il download
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        // Pulisci l'URL del blob
        window.URL.revokeObjectURL(blobUrl);

        this.messageService.add({
          severity: 'success',
          summary: 'Download',
          detail: `Download di ${file.nome} avviato`,
          life: 3000
        });
      })
      .catch(error => {
        this.logger.error('[LISTA FILE] Errore durante il download', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Errore',
          detail: `Errore durante il download di ${file.nome}`,
          life: 3000
        });
      });
  }

  /**
   * Mostra la modale di conferma per l'eliminazione di un singolo file
   */
  mostraConfermaEliminazioneFile(file: FileInfo) {
    this.logger.info('[LISTA FILE] Richiesta eliminazione singolo file', file.nome);
    this.fileSelezionatoPerEliminazione = file;
    this.showConfermaEliminazioneSingoloFile = true;
  }

  /**
   * Nasconde la modale di conferma eliminazione singolo file
   */
  onHideConfermaEliminazioneSingoloFile() {
    if (this.eliminazioneSingoloFileInCorso) {
      return;
    }
    this.showConfermaEliminazioneSingoloFile = false;
    this.fileSelezionatoPerEliminazione = null;
  }

  /**
   * Annulla l'operazione di eliminazione singolo file
   */
  annullaSingoloFile() {
    this.showConfermaEliminazioneSingoloFile = false;
    this.fileSelezionatoPerEliminazione = null;
  }

  /**
   * Conferma ed esegue l'eliminazione del singolo file
   */
  confermaEliminazioneSingoloFile() {
    if (!this.fileSelezionatoPerEliminazione) {
      this.messageService.add({
        severity: 'error',
        summary: 'Errore',
        detail: 'Nessun file selezionato per l\'eliminazione',
        life: 3000
      });
      return;
    }

    this.eliminazioneSingoloFileInCorso = true;
    const fileToDelete = this.fileSelezionatoPerEliminazione;

    this.logger.info('[LISTA FILE] Inizio eliminazione singolo file', fileToDelete.nome);

    // Estrai il path relativo dall'URL
    const relativePath = this.extractRelativePathFromUrl(fileToDelete.url);

    this.firebaseStorageService.deleteFile(relativePath).subscribe({
      next: () => {
        this.logger.info(`[LISTA FILE] File eliminato con successo: ${fileToDelete.nome}`);

        // Rimuovi il file dalla lista locale
        this.files = this.files.filter(f => f.url !== fileToDelete.url);

        this.messageService.add({
          severity: 'success',
          summary: 'Successo',
          detail: `File "${fileToDelete.nome}" eliminato con successo`,
          life: 5000
        });

        this.eliminazioneSingoloFileInCorso = false;
        this.showConfermaEliminazioneSingoloFile = false;
        this.fileSelezionatoPerEliminazione = null;
      },
      error: (error) => {
        this.logger.error(`[LISTA FILE] Errore nell'eliminazione del file: ${fileToDelete.nome}`, error);

        this.messageService.add({
          severity: 'error',
          summary: 'Errore',
          detail: `Errore nell'eliminazione del file "${fileToDelete.nome}"`,
          life: 5000
        });

        this.eliminazioneSingoloFileInCorso = false;
      }
    });
  }

  /**
   * Estrae il path relativo da un URL completo di Firebase Storage
   * (Riutilizza il metodo già esistente nel componente stato-rendicontazione)
   */
  private extractRelativePathFromUrl(fullUrl: string): string {
    try {
      const url = new URL(fullUrl);
      const pathSegments = url.pathname.split('/');

      // Trova l'indice del segmento 'o' che precede il path del file
      const oIndex = pathSegments.findIndex(segment => segment === 'o');
      if (oIndex !== -1 && oIndex + 1 < pathSegments.length) {
        // Prendi tutto dopo '/o/' e decodifica
        const encodedPath = pathSegments.slice(oIndex + 1).join('/');
        return decodeURIComponent(encodedPath);
      }

      // Fallback: usa l'URL originale
      this.logger.warn('[LISTA FILE] Impossibile estrarre path relativo, uso URL completo', fullUrl);
      return fullUrl;
    } catch (error) {
      this.logger.error('[LISTA FILE] Errore nell\'estrazione del path relativo', error);
      return fullUrl;
    }
  }
}
